import { useRef } from "react";
import { motion, useScroll, useTransform, useSpring, useReducedMotion } from "framer-motion";
import { Reveal, MaskLine } from "./Reveal";

export const PageHero = ({ eyebrow, lines, text, image, imageAlt }) => {
  const ref = useRef(null);
  const prefersReduced = useReducedMotion();

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  // Spring-smoothed scroll so transforms feel cinematic, not jittery.
  const smooth = useSpring(scrollYProgress, { stiffness: 110, damping: 24, mass: 0.35 });

  // 3D depth: scale + real translateZ on the image, graceful scale/fade on the text.
  // All transforms + opacity only → GPU-accelerated. Reduced motion holds identity.
  const imgScale = useTransform(smooth, [0, 1], prefersReduced ? [1, 1] : [1, 1.28]);
  const imgTranslateZ = useTransform(smooth, [0, 1], prefersReduced ? [0, 0] : [0, 110]);
  const imgY = useTransform(smooth, [0, 1], prefersReduced ? ["0%", "0%"] : ["0%", "12%"]);
  const textScale = useTransform(smooth, [0, 0.6], prefersReduced ? [1, 1] : [1, 0.94]);
  const textY = useTransform(smooth, [0, 0.6], prefersReduced ? [0, 0] : [0, -32]);
  const textOpacity = useTransform(smooth, [0, 0.55, 0.85], prefersReduced ? [1, 1, 1] : [1, 0.5, 0]);

  return (
    <section
      ref={ref}
      data-testid="page-hero"
      className="hero-perspective relative min-h-[82vh] bg-forest-deep text-cream overflow-hidden flex items-end grain"
    >
      <motion.div
        style={{ scale: imgScale, translateZ: imgTranslateZ, y: imgY }}
        className="hero-3d absolute inset-0 will-change-transform"
      >
        <img
          src={image}
          alt={imageAlt}
          fetchPriority="high"
          className="w-full h-[120%] object-cover"
        />
      </motion.div>
      <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-r from-forest-deep/95 via-forest/70 to-forest-deep/30" />
      <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-forest-deep/95 via-forest-deep/20 to-forest-deep/40" />
      <motion.div
        style={{ opacity: textOpacity, scale: textScale, y: textY }}
        className="wrap-x relative z-[2] w-full pb-16 sm:pb-24 pt-44 will-change-transform"
      >
        <Reveal y={16}>
          <p className="eyebrow text-gold-soft" data-testid="page-hero-eyebrow">{eyebrow}</p>
        </Reveal>
        <h1 className="font-serif font-medium tracking-tight text-4xl sm:text-5xl lg:text-[64px] leading-[1.04] mt-6 max-w-3xl">
          {lines.map((line, i) => (
            <MaskLine key={i} delay={0.15 + i * 0.12}>
              {line}
            </MaskLine>
          ))}
        </h1>
        {text && (
          <Reveal delay={0.5}>
            <p className="mt-7 max-w-xl text-[15px] leading-[1.8] text-cream/75">{text}</p>
          </Reveal>
        )}
      </motion.div>
      <div aria-hidden="true" className="absolute bottom-0 left-0 right-0 h-px bg-gold/25 z-[2]" />
    </section>
  );
};
